import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_E2E_GET_AASO_BY_APPLICANT } from 'src/api/e2e-request-objects';
import {
  E2eBaseCallDetailsI,
  e2eGetBaseAuthenticatedRequests,
  e2eGetBasePublicRequests,
} from 'src/api/query-logic/e2e-query-base-calls';
import { apiRequest } from 'src/api/request-handler';
import {
  breadHandleE2eRoutingLogic,
  getFullE2ePath,
} from 'src/e2e-redesign/business-logic/bread-logic/bread-routing-logic';
import { E2eLogicObjectsWithParams } from 'src/e2e-redesign/business-logic/e2e-logic-utils';
import {
  E2eRoutingArgsI,
  E2eRoutingLogicI,
  handleE2eLoginLogicRoute,
  handleE2eRoutingLogic,
} from 'src/e2e-redesign/business-logic/e2e-routing-logic';
import { ComponentLogicI } from 'src/e2e-redesign/interfaces/e2e-base-interfaces';
import useE2eStore from 'src/e2e-store';
import { ApplicationStage } from 'src/enums/aaso.enums';
import { E2ERoutes, NewE2ERoutes } from 'src/enums/routes.enums';
import { useSearchParams } from 'src/hooks/use-search-params';
import { E2eGlobalStore } from 'src/interfaces/e2e-state.interfaces';
import { routeTransitionMap } from 'src/interfaces/transition-maps';
import { BREAD_LENDER_ID } from 'src/utils/bread-e2e-constants';
import { isSafeReturnTo } from 'src/utils/safe-redirect';

export const newLoginLogic_init = (routingPackage: E2eLogicObjectsWithParams) => {
  const params = routingPackage.params;
  const { store_uuid, e2e_form_id } = params;
  const { e2eStore, auth } = routingPackage;
  const { isAuthenticated } = auth;
  const baseCallDetails: E2eBaseCallDetailsI = {
    store_uuid: params.store_uuid,
    e2e_form_id: params.e2e_form_id,
  };
  // this runs if user is not authenticated
  if (baseCallDetails.store_uuid && baseCallDetails.e2e_form_id) {
    e2eGetBasePublicRequests(e2eStore, baseCallDetails, isAuthenticated);
    // this runs if they are
    e2eGetBaseAuthenticatedRequests(baseCallDetails, e2eStore, isAuthenticated);
  }

  // we can add useEffects here too if we wanted
  useEffect(() => {
    if (store_uuid && e2e_form_id) {
      // if exist add to e2e store
      useE2eStore.setState({ store_uuid, e2e_form_id });
    }
  }, [store_uuid, e2e_form_id]);
};

export interface NewLoginFunctionsI extends ComponentLogicI {
  fn: {
    handleContinueExistingApp: (continueRoute: any | undefined, continueAASO: any) => void;
    handleAASORedirect: () => void;
    handleCreateNewApp: (returnTo: string) => void;
  };
  v: {
    continueRoute: string;
    continueAASO: any;
    openContinueApp: boolean;
    returnTo: string | null;
  };
}
export const newLoginLogic_functionsAndVals = (routingPackage: E2eLogicObjectsWithParams): NewLoginFunctionsI => {
  const { store_uuid, e2e_form_id } = routingPackage.params;
  const { auth } = routingPackage;
  const { user } = auth;
  const createAaso = useE2eStore((state: E2eGlobalStore) => state.createAaso);
  const setAaso = useE2eStore((state: E2eGlobalStore) => state.setAASO);
  const [continueRoute, setContinueRoute] = useState<any>();
  const [continueAASO, setContinueAASO] = useState<any>();
  const [openContinueApp, setOpenContinueApp] = useState<boolean>(false);

  const returnTo = useSearchParams().get('returnTo');

  const handleShowContinue = (continueRoute: string, continueAASO: any) => {
    setContinueAASO(continueAASO);
    setOpenContinueApp(true);
    setContinueRoute(continueRoute);
  };
  const handleReturnTo = async (returnTo: string, AASO?: any) => {
    if (!AASO?._id && store_uuid && e2e_form_id) {
      // create new aaso
      const generatedAASO = await createAaso(user, store_uuid, e2e_form_id);
      const newRoutingPackage: E2eRoutingLogicI = {
        ...routingPackage,
        aasoContext: { aaso: generatedAASO },
      };
      const routingArgs: E2eRoutingArgsI = {
        currentStage: ApplicationStage.STARTED,
        currentE2eRoute: E2ERoutes.Welcome,
        aaso: generatedAASO,
        continueRoute: returnTo,
      };
      handleE2eLoginLogicRoute(newRoutingPackage, routingArgs, e2e_form_id);
      return;
    }
    // default if nothing works - will redirect them to dashboard
    if (isSafeReturnTo(returnTo)) {
      return window.location.replace(returnTo);
    }
    return routingPackage.navigate('/dashboard');
  };

  const handleCreateNewAASOForPaymentOptions = async () => {
    if (store_uuid && e2e_form_id) {
      // This creates a new AASO and returns it
      const generatedAASO = await createAaso(user, store_uuid, e2e_form_id);
      if (
        generatedAASO &&
        generatedAASO.store_name &&
        generatedAASO.store_uuid &&
        generatedAASO._id &&
        generatedAASO.e2e_form_id
      ) {
        const path = getFullE2ePath(
          generatedAASO.store_name,
          generatedAASO.store_uuid,
          generatedAASO._id,
          generatedAASO.e2e_form_id,
          NewE2ERoutes.PaymentOptions,
        );
        routingPackage.navigate(path);
        return;
      }
      // fallback
      routingPackage.navigate('/dashboard');
    }
  };

  const handleContinueExistingApp = (continueRoute: any | undefined, continueAASO: any) => {
    const newRoutingPackage: E2eRoutingLogicI = {
      ...routingPackage,
      aasoContext: { aaso: continueAASO },
    };
    setAaso(continueAASO);
    const routingArgs: E2eRoutingArgsI = {
      currentStage: continueAASO.current_stage,
      currentE2eRoute: E2ERoutes.Welcome, // this doesn't matter
      aaso: continueAASO,
      continueRoute,
      continue: true,
    };
    let hasBread = false;
    const lenderIds = continueAASO?.e2e_form_snapshot?.paths?.reduce((acc: number[], path: any) => {
      return [...acc, ...path.lenders];
    }, []);

    if (lenderIds && lenderIds.includes(BREAD_LENDER_ID)) {
      hasBread = true;
    }

    if (hasBread) {
      breadHandleE2eRoutingLogic(newRoutingPackage, routingArgs, continueAASO);
    } else {
      handleE2eRoutingLogic(newRoutingPackage, routingArgs, continueAASO);
    }
  };

  // const handleAASORedirect = async () => {
  //   console.log('handleAASORedirect login logic');
  //   if (user) {
  //     let AASO;
  //     try {
  //       if (store_uuid) {
  //         // check to see if an existing aaso already exists
  //         AASO = await apiRequest(API_E2E_GET_AASO_BY_APPLICANT, {
  //           params: { applicant_id: user?.id, store_uuid, e2e_form_id },
  //         });
  //       }
  //     } catch (e) {
  //     } finally {
  //       // aaso does not exists, create new
  //       if (!AASO?._id && returnTo) return handleReturnTo(returnTo);

  //       if (AASO?._id) {
  //         const paymentType = window.localStorage.getItem('paymentType');
  //         if (paymentType) {
  //           console.log('paymentType found');
  //           const { store_name, store_uuid, _id, e2e_form_id } = AASO;
  //           if (store_name && store_uuid && _id && e2e_form_id) {
  //             const path = getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.PaymentOptions);
  //             routingPackage.navigate(path);
  //             return;
  //           }
  //         }
  //         // aaso already exists
  //         const routeFromStage = routeTransitionMap[AASO.current_stage as keyof typeof routeTransitionMap];
  //         if (routeFromStage) {
  //           return handleShowContinue(routeFromStage, { ...AASO });
  //         }

  //         if (returnTo) return handleReturnTo(returnTo, AASO);
  //       } else {
  //         routingPackage.navigate('/dashboard');
  //       }
  //     }
  //   }
  // };

  const handleAASORedirect = async () => {
    console.log('handleAASORedirect login logic');
    if (user) {
      let AASO;
      const source = window.localStorage.getItem('source');
      const paymentType = window.localStorage.getItem('paymentType');
      try {
        if (store_uuid) {
          // check to see if an existing aaso already exists
          AASO = await apiRequest(API_E2E_GET_AASO_BY_APPLICANT, {
            params: { applicant_id: user?.id, store_uuid, e2e_form_id },
          });
        }
      } catch (e) {
      } finally {
        // If this is CandidPay handle navigating to the payment options page
        if (source && paymentType) {
          // If no AASO or no source_id, create new (handleReturnTo if returnTo exists)
          if (!AASO?._id) return await handleCreateNewAASOForPaymentOptions();
          // If source_id matches, use existing and redirect to payment options
          if (AASO.source_id === source) {
            const { store_name, store_uuid, _id, e2e_form_id } = AASO;
            if (store_name && store_uuid && _id && e2e_form_id) {
              const path = getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.PaymentOptions);
              routingPackage.navigate(path);
              return;
            }
          }
          // If source_id exists and is different, create new (handleReturnTo if returnTo exists)
          if (AASO.source_id !== source) return await handleCreateNewAASOForPaymentOptions();
          return;
        }
        if (AASO?._id) {
          const paymentType = window.localStorage.getItem('paymentType');
          if (paymentType) {
            console.log('paymentType found');
            const { store_name, store_uuid, _id, e2e_form_id } = AASO;
            if (store_name && store_uuid && _id && e2e_form_id) {
              const path = getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.PaymentOptions);
              routingPackage.navigate(path);
              return;
            }
          }
          // aaso already exists
          const routeFromStage = routeTransitionMap[AASO.current_stage as keyof typeof routeTransitionMap];
          if (routeFromStage) {
            return handleShowContinue(routeFromStage, { ...AASO });
          }

          if (returnTo) return handleReturnTo(returnTo, AASO);
        } else {
          if (returnTo) return handleReturnTo(returnTo, AASO);
          else routingPackage.navigate('/dashboard');
        }
      }
    }
  };

  const handleCreateNewApp = async (returnTo: string) => {
    if (store_uuid && e2e_form_id) {
      const generatedAASO = await createAaso(user, store_uuid, e2e_form_id);
      const routingArgs: E2eRoutingArgsI = {
        currentStage: ApplicationStage.STARTED,
        currentE2eRoute: E2ERoutes.Welcome,
        aaso: generatedAASO,
        continueRoute: returnTo,
      };
      handleE2eLoginLogicRoute(routingPackage, routingArgs, e2e_form_id);
    }
  };

  return {
    fn: {
      handleContinueExistingApp,
      handleAASORedirect,
      handleCreateNewApp,
    },
    v: {
      continueAASO,
      continueRoute,
      openContinueApp,
      returnTo,
    },
  };
};
